<template>
  <div id="app" class="data-screen" :class="{ 'mobile-layout': isMobile, 'desktop-layout': !isMobile, 'data-loading': loading }">
    <!-- Toast 提示组件 -->
    <Toast ref="toast" />
    
    <!-- 加载动画 - 改为小型加载指示器 -->
    <transition name="fade">
      <div v-if="loading" class="loading-indicator">
        <div class="loading-spinner-small"></div>
      </div>
    </transition>
    
    <!-- 头部标题 -->
    <div class="screen-header">
      <!-- 届次切换按钮组 -->
      <div class="session-buttons">
        <div class="session-buttons-wrapper" ref="buttonsWrapper">
          
          <div class="session-buttons-container">
            <button 
              :class="['session-btn', { active: currentSession === 'all' }]"
              @click="handleSessionChange('all')"
              :disabled="loading"
            >
              全部
            </button>
            <button 
              v-for="session in sessions" 
              :key="session.id"
              :class="['session-btn', { active: currentSession === session.id }]"
              @click="handleSessionChange(session.id)"
              :disabled="loading"
            >
              {{ session.name }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 移动端布局 -->
    <template v-if="isMobile">
      <div class="mobile-container">
        <div class="mobile-section map-section">
          <CenterMap />
        </div>
        <div class="mobile-section left-section">
          <LeftPanel />
        </div>
        <div class="mobile-section right-section">
          <RightPanel />
        </div>
      </div>
    </template>

    <!-- PC 端布局（保持原有结构） -->
    <template v-else>
      <!-- 左侧面板 -->
      <div class="screen-left">
        <LeftPanel />
      </div>

      <!-- 中间地图 -->
      <div class="screen-center">
        <CenterMap />
      </div>

      <!-- 右侧面板 -->
      <div class="screen-right">
        <RightPanel />
      </div>
    </template>

    <!-- 底部装饰 -->
    <div class="screen-footer"></div>
  </div>
</template>

<script>
import LeftPanel from './components/LeftPanel.vue'
import CenterMap from './components/CenterMap.vue'
import RightPanel from './components/RightPanel.vue'
import Toast from './components/Toast.vue'
import EventBus from './utils/eventBus'
import dataService from './services/dataService'

export default {
  name: 'App',
  components: {
    LeftPanel,
    CenterMap,
    RightPanel,
    Toast
  },
  data() {
    return {
      currentSession: 'all',  // 当前选中的届次（默认全部）
      sessions: [
        { id: 6, name: '第六届' },
        { id: 5, name: '第五届' },
        { id: 4, name: '第四届' },
        { id: 3, name: '第三届' },
        { id: 2, name: '第二届' },
        { id: 1, name: '第一届' }
      ],
      loading: false,  // 加载状态
      error: null,  // 错误信息
      debounceTimer: null,  // 防抖定时器
      // 设备检测相关
      isMobile: false,  // 是否为移动设备
      windowWidth: 0,  // 当前窗口宽度
      resizeTimer: null  // resize 防抖定时器
    }
  },
  created() {
    // 页面加载时加载默认届次数据
    this.loadSessionData(this.currentSession)
    
    // 监听错误事件
    EventBus.$on('show-error', this.showError)
  },
  mounted() {
    // 设备检测
    this.checkDevice()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    // 取消订阅错误事件
    EventBus.$off('show-error', this.showError)
    
    // 移除 resize 事件监听
    window.removeEventListener('resize', this.handleResize)
    
    // 清理定时器
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
    }
  },
  methods: {
    /**
     * 检测设备类型
     * 根据视口宽度判断是否为移动设备（< 768px）
     */
    checkDevice() {
      this.windowWidth = window.innerWidth || document.documentElement.clientWidth || 0
      this.isMobile = this.windowWidth < 768
    },
    
    /**
     * 处理窗口大小变化（带 150ms 防抖）
     */
    handleResize() {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        this.checkDevice()
      }, 150)
    },
    
    // 处理届次切换
    async handleSessionChange(session) {
      // 如果点击的是当前届次，不做任何操作（幂等性）
      if (session === this.currentSession) {
        return
      }
      
      // 清除之前的防抖定时器
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }
      
      // 使用防抖机制（300ms）
      this.debounceTimer = setTimeout(async () => {
        // 更新当前届次
        this.currentSession = session
        
        // 加载届次数据
        await this.loadSessionData(session)
      }, 300)
    },
    
    // 加载届次数据
    async loadSessionData(session) {
      try {
        // 设置加载状态
        this.loading = true
        this.error = null
        
        // 获取届次数据
        const data = await dataService.getSessionData(session)
        
        // 触发 session-changed 事件，通知所有子组件
        EventBus.$emit('session-changed', {
          session: session,
          data: data
        })
        
        // 清除加载状态
        this.loading = false
      } catch (error) {
        // 处理错误
        console.error('加载届次数据失败:', error)
        this.error = error.message || '加载数据失败，请稍后重试'
        this.loading = false
        
        // 显示错误提示
        this.showError(this.error)
      }
    },
    
    /**
     * 显示错误提示
     * @param {string} message - 错误消息
     */
    showError(message) {
      if (this.$refs.toast) {
        // 根据错误类型显示不同的提示
        let type = 'error'
        let displayMessage = message
        
        // 网络错误
        if (message.includes('网络') || message.includes('Network')) {
          type = 'error'
          displayMessage = '网络连接失败，请检查网络连接后重试'
        }
        // 超时错误
        else if (message.includes('超时') || message.includes('timeout')) {
          type = 'warning'
          displayMessage = '请求超时，请稍后重试'
        }
        // 数据格式错误
        else if (message.includes('格式') || message.includes('format')) {
          type = 'error'
          displayMessage = '数据格式错误，请联系管理员'
          console.error('数据格式错误:', message)
        }
        
        this.$refs.toast.show({
          message: displayMessage,
          type: type,
          duration: 3000
        })
      }
    }
  }
}
</script>

<style lang="scss">
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

#app {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-attachment: fixed;
  color: #fff;
  font-family: 'Microsoft YaHei', Arial, sans-serif;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('~@/assets/images/背景图.png') no-repeat center center;
    background-size: cover;
    pointer-events: none;
    z-index: 0;
    filter: brightness(0.6);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 11vh;
    background: url('~@/assets/images/湖南省体育云动会数据大屏顶部背景图.png') no-repeat top center;
    background-size: cover;
    pointer-events: none;
    z-index: 1;
  }
}

.data-screen {
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 1;
}

// 小型加载指示器（右上角）
.loading-indicator {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.8);
  padding: 10px 15px;
  border-radius: 8px;
  border: 1px solid #4facfe;
  box-shadow: 0 0 20px rgba(79, 172, 254, 0.5);
  display: flex;
  align-items: center;
  gap: 10px;
  
  .loading-spinner-small {
    width: 20px;
    height: 20px;
    border: 3px solid rgba(79, 172, 254, 0.3);
    border-top-color: #4facfe;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
}

// 淡入淡出过渡效果
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.screen-header {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 0px;
  position: relative;
  padding-top: 9.5vh;
  z-index: 2;
  
  .session-buttons {
    display: flex;
    gap: 12px;
    align-items: center;
    width: 100%;
    position: relative;
    justify-content: center;  // 默认居中对齐
    
    .session-buttons-wrapper {
      overflow-x: auto;
      overflow-y: hidden;
      position: relative;
      scroll-behavior: smooth;
      display: flex;
      justify-content: center;  // 让内部容器居中
      
      &::-webkit-scrollbar {
        display: none;
      }
      
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
    
    .session-buttons-container {
      display: flex;
      gap: 12px;
      align-items: center;
      white-space: nowrap;
      justify-content: center;  // 按钮组居中
      padding: 0 5px;
    }
    
    .session-btn {
      padding: 0vh 1vh;
      background: rgba(79, 172, 254, 0.15);
      border: 1px solid rgba(79, 172, 254, 0.4);
      color: #8c8c8c;
      cursor: pointer;
      border-radius: 20px;
      font-size: 1.5vh;
      transition: all 0.3s;
      position: relative;
      overflow: hidden;
      
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(79, 172, 254, 0.3), transparent);
        transition: left 0.5s;
      }
      
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      &.active {
        background: linear-gradient(135deg, #4facfe, #00f2fe);
        border-color: #4facfe;
        color: #fff;
        box-shadow: 0 0 15px rgba(79, 172, 254, 0.6);
        font-weight: bold;
        
        &::before {
          left: 100%;
        }
      }
      
      &.special {
        background: rgba(255, 107, 109, 0.15);
        border-color: rgba(255, 107, 109, 0.4);
        color: #ff6b6d;
        
        &:hover {
          background: rgba(255, 107, 109, 0.25);
          border-color: #ff6b6d;
          box-shadow: 0 0 10px rgba(255, 107, 109, 0.4);
        }
      }
      
      &:hover:not(.active):not(.special):not(:disabled) {
        background: rgba(79, 172, 254, 0.25);
        border-color: #4facfe;
        color: #4facfe;
        box-shadow: 0 0 10px rgba(79, 172, 254, 0.4);
      }
    }
  }
}

.screen-left,
.screen-center,
.screen-right {
  position: absolute;
  top: 0px;
  bottom: 4vh;
  width: 33.33%;
}

.screen-left,
.screen-right {
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 0 20px 20px;
  overflow-y: auto;
  top: 7.5vh;
}

.screen-left {
  left: 0;
}

.screen-right {
  right: 0;
}

.screen-center {
  left: 33.33%;
  top: 11.5vh;
}

.screen-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4vh;
  background: url('~@/assets/images/整个页面的底部背景图.png') no-repeat center center;
  background-size: contain;
}

// ==================== 移动端响应式样式 ====================
// 移动端布局样式
.mobile-layout {
  .screen-header {
    position: relative;
    height: auto;
    padding: 10px;
    padding-top: 80px; // 为顶部背景图留出空间
    
    .session-buttons {
      width: 100%;
      display: flex;
      gap: 8px;
      align-items: center;
      justify-content: center;  // 移动端居中
      
      .session-buttons-wrapper {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        flex: 1;
        
        &::-webkit-scrollbar {
          height: 4px;
        }
        
        &::-webkit-scrollbar-thumb {
          background: rgba(79, 172, 254, 0.5);
          border-radius: 2px;
        }
      }
      
      .session-buttons-container {
        display: flex;
        gap: 10px;
        padding: 5px 0;
      }
      
      // 问题 1 & 2: 届次按钮样式优化 - 一屏显示全部，不需要滚动
      .session-btn {
        font-size: 10px;  // 减小字体
        padding: 6px 8px;  // 减小内边距
        min-width: 50px;  // 减小最小宽度
        min-height: 32px;  // 减小最小高度
        touch-action: manipulation;
        flex-shrink: 0;
        white-space: nowrap;
        background: rgba(15, 45, 75, 0.6);
        border: 1px solid rgba(79, 172, 254, 0.4);
        border-radius: 12px;  // 减小圆角
        
        &.active {
          background: linear-gradient(135deg, rgba(79, 172, 254, 0.4), rgba(0, 242, 254, 0.4));
          border-color: #4facfe;
          box-shadow: 0 0 10px rgba(79, 172, 254, 0.5);
        }
        
        &:hover:not(.active):not(:disabled) {
          background: rgba(79, 172, 254, 0.2);
          border-color: rgba(79, 172, 254, 0.6);
        }
      }
    }
  }
  
  .mobile-container {
    width: 100%;
    height: calc(100vh - 120px); // 减去头部高度
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 10px;
    position: relative;
    z-index: 1;
    scroll-behavior: smooth;
  }
  
  .mobile-section {
    width: 100%;
    margin-bottom: 20px;
    background: rgba(15, 45, 75, 0.8);
    border-radius: 8px;
    padding: 10px;
    
    &.map-section {
      min-height: 500px;
      height: auto;
    }
    
    &.left-section {
      min-height: 800px;
      height: auto;
    }
    
    &.right-section {
      min-height: 800px;
      height: auto;
    }
  }
  
  // 隐藏或调整装饰元素
  &::after {
    height: 80px; // 缩小顶部背景图高度
  }
  
  .screen-footer {
    display: none; // 移动端隐藏底部装饰
  }
  
  // 隐藏 PC 端布局元素
  .screen-left,
  .screen-center,
  .screen-right {
    display: none;
  }
}

// 桌面端布局样式（保持原有，添加类名以提高优先级）
.desktop-layout {
  // PC 端头部样式：按钮居中
  .screen-header {
    justify-content: center;  // PC 端居中显示
  }
  
  .screen-left,
  .screen-center,
  .screen-right {
    position: absolute;
    top: 0px;
    bottom: 4vh;
    width: 33.33%;
  }
  
  .screen-left,
  .screen-right {
    display: flex;
    flex-direction: column;
    gap: 15px;
    padding: 0 20px 20px;
    overflow-y: auto;
    top: 7.5vh;
  }
  
  .screen-left {
    left: 0;
  }
  
  .screen-right {
    right: 0;
  }
  
  .screen-center {
    left: 33.33%;
    top: 11.5vh;
  }
  
  // 隐藏移动端布局元素
  .mobile-container {
    display: none;
  }
}

// 媒体查询：针对小屏幕设备的额外优化
@media (max-width: 767px) {
  // 字体大小调整
  html {
    font-size: 14px;
  }
  
  // 确保所有可交互元素的触摸目标足够大
  .session-btn,
  .tab,
  .carousel-arrow,
  .indicator {
    min-width: 44px;
    min-height: 44px;
  }
  
  // 图表容器调整
  .chart-container,
  .chart-container-large {
    min-height: 250px;
  }
  
  // 面板内边距调整
  .panel-content {
    padding: 10px;
  }
  
  // 面板标题字体调整
  .panel-title {
    font-size: 14px;
  }
  
  // 统计数据字体调整
  .stat-label {
    font-size: 11px;
  }
  
  .stat-value {
    font-size: 16px;
  }
}

// 媒体查询：针对超小屏幕设备（如 iPhone SE）
@media (max-width: 375px) {
  .mobile-section {
    &.map-section {
      min-height: 300px;
    }
    
    &.left-section,
    &.right-section {
      min-height: 250px;
    }
  }
  
  .panel-title {
    font-size: 13px;
  }
}
</style>
